import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, Icon, Message, Textarea, Typhography } from 'components/core';
import Modal from 'components/core/Modals/Modal/Modal';
import { useMarkPbtCanceledMutation, useMarkPbtRefundedMutation } from 'services/payoutsApi';

export type MarkPbtStatusAction = 'refund' | 'cancel';

export interface MarkPbtStatusTransfer {
  id: string;
  payup_transfer_number?: number | string | null;
  batch_number?: number | string | null;
  pay_mode?: string | null;
  status?: string | null;
  amount?: string | number | null;
  property?: { name?: string | null } | null;
  account?: {
    business_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  } | null;
}

interface MarkPbtStatusModalProps {
  isOpen: boolean;
  action: MarkPbtStatusAction;
  transfer: MarkPbtStatusTransfer | null;
  onClose: () => void;
}

interface FormValues {
  notes: string;
}

const formatAmount = (amount: MarkPbtStatusTransfer['amount']) => {
  if (amount === null || amount === undefined) return '';
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (Number.isNaN(n)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(n);
};

const accountLabel = (transfer: MarkPbtStatusTransfer | null) => {
  if (!transfer?.account) return '';
  const acct = transfer.account;
  if (acct.business_name) return acct.business_name;
  return [acct.first_name, acct.last_name].filter(Boolean).join(' ');
};

export const MarkPbtStatusModal = ({
  isOpen,
  action,
  transfer,
  onClose,
}: MarkPbtStatusModalProps) => {
  const [markRefunded, { isLoading: refundLoading }] = useMarkPbtRefundedMutation();
  const [markCanceled, { isLoading: cancelLoading }] = useMarkPbtCanceledMutation();
  const isLoading = refundLoading || cancelLoading;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { register, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: { notes: '' },
  });

  useEffect(() => {
    if (isOpen) {
      reset({ notes: '' });
      setErrorMessage(null);
      setSuccess(false);
    }
  }, [isOpen, transfer?.id, action, reset]);

  if (!isOpen || !transfer) return null;

  const verb = action === 'refund' ? 'refunded' : 'canceled';
  const title = action === 'refund' ? 'Mark Transfer Refunded' : 'Mark Transfer Canceled';

  const onSubmit = async ({ notes }: FormValues) => {
    setErrorMessage(null);
    const args = { id: transfer.id, notes: notes.trim() || undefined };
    const mutation = action === 'refund' ? markRefunded : markCanceled;
    const response: any = await mutation(args);
    if (response?.error) {
      setErrorMessage(
        response.error?.data?.message ??
          response.error?.data?.error ??
          'Something went wrong. Please try again.'
      );
      return;
    }
    setSuccess(true);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      modalAdditionalClasses={
        '!w-[440px] flex flex-col bg-white justify-center rounded-lg shadow-importInvoicesModal pt-6 pb-6 px-6'
      }
    >
      <div className="absolute cursor-pointer top-4 right-4 text-grey-600" onClick={onClose}>
        <Icon iconColor="inherit" iconType="CloseIcon" />
      </div>

      {!success && (
        <>
          <Typhography component="p" type="h2" color="text-grey-900 mb-4 uppercase">
            {title}
          </Typhography>

          <div className="flex flex-col w-full mb-4 text-13s text-gray-700">
            <span>
              You are about to mark the following transfer as <strong>{verb}</strong>. The change is
              recorded against your user and cannot be undone from the dashboard.
            </span>
          </div>

          <div className="flex flex-col w-full px-3 py-3 mb-4 text-12s bg-gray-100 rounded-md">
            <span className="text-gray-600">PayUp transfer #</span>
            <span className="mb-2 font-medium text-gray-900">
              {transfer.payup_transfer_number ?? '—'}
            </span>
            <span className="text-gray-600">Batch #</span>
            <span className="mb-2 font-medium text-gray-900">{transfer.batch_number ?? '—'}</span>
            {transfer.property?.name && (
              <>
                <span className="text-gray-600">Property</span>
                <span className="mb-2 font-medium text-gray-900">{transfer.property.name}</span>
              </>
            )}
            {accountLabel(transfer) && (
              <>
                <span className="text-gray-600">Vendor</span>
                <span className="mb-2 font-medium text-gray-900">{accountLabel(transfer)}</span>
              </>
            )}
            <span className="text-gray-600">Amount</span>
            <span className="mb-2 font-medium text-gray-900">{formatAmount(transfer.amount)}</span>
            <span className="text-gray-600">Pay mode / status</span>
            <span className="font-medium text-gray-900">
              {transfer.pay_mode ?? '—'} / {transfer.status ?? '—'}
            </span>
          </div>

          {errorMessage && (
            <Message type="error" additionalClasses="mb-4">
              {errorMessage}
            </Message>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col w-full" noValidate>
            <Textarea
              id="notes"
              labelName="notes"
              label="Notes (optional)"
              {...register('notes')}
            />
            <div className="flex flex-row items-center justify-end w-full mt-4">
              <span
                className={`inline-flex font-bold text-14s text-black-900 mr-7 ${
                  isLoading
                    ? 'pointer-events-none opacity-50'
                    : 'pointer-events-auto cursor-pointer'
                }`}
                onClick={isLoading ? undefined : onClose}
              >
                Cancel
              </span>
              <Button
                type="submit"
                buttonLabel={action === 'refund' ? 'Mark Refunded' : 'Mark Canceled'}
                variants="extraSmall"
                additionalClasses="w-[160px]"
                iconType="Spin"
                iconClass="inline-flex ml-1 w-3 h-3"
                isIconEnabled={isLoading}
                iconAlignment="right"
              />
            </div>
          </form>
        </>
      )}

      {success && (
        <div className="flex flex-col items-center justify-center w-full py-8">
          <Icon iconType="TickIconForSuccess" />
          <span className="flex justify-center w-full mt-4 mb-1 font-medium text-20s">
            Transfer marked {verb}
          </span>
          <span className="mb-6 font-light text-12s text-gray-600">
            The transactions list will refresh automatically.
          </span>
          <Button
            type="button"
            buttonLabel="Close"
            variants="extraSmall"
            additionalClasses="w-[120px]"
            onClick={onClose}
          />
        </div>
      )}
    </Modal>
  );
};

export default MarkPbtStatusModal;
